import styled from "styled-components";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Tooltip } from "@mui/material";
import { signal } from "@preact/signals-react";
import {
  useFetchEntities,
  useRemoveTourGroupFromUserSchedule,
  useAddOrUpdateUserDaySchedule,
} from "@/reactQueryHooks";
import { refreshStaffScheduleById } from "../staff_planner/signalStore";
import CloseIcon from "@mui/icons-material/Close";

interface AssignedRoleBadgeProps {
  tourGroupId: string;
  assigneeUsd: any;
  refetch: () => void;
}

interface TourGroup {
  _id: string;
  product_id: string;
  option_id: string;
  start_time_id: string;
  vehicle_id: string;
}

interface Comment {
  _id: string;
  author_id: string;
  date: string;
  text: string;
}

interface TourGroupSchedule {
  id: string;
  role: string;
  projected?: boolean;
  _data?: TourGroup;
}

interface ScheduleData {
  _id?: string;
  tourGroups: TourGroupSchedule[];
  comments?: Comment[];
  isDayOff?: boolean;
  isLeave?: boolean;
  isProjectedDayOff?: boolean;
  isProjectedLeave?: boolean;
  request?: {
    requestBody: string;
    author_id: string;
    date: string;
  };
  user?: string;
  date?: string;
  notify?: boolean;
}

const BadgeContainer = styled.div<{
  $isProjected?: boolean;
}>`
  display: flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 18px;
  background-color: ${({ $isProjected }) =>
    $isProjected ? "rgba(255, 153, 0, 0.205)" : "#c5f2ddc8"};
  color: ${({ $isProjected }) =>
    $isProjected ? "rgba(151, 91, 0, 0.8)" : "#2e7d5b"};
  font-size: 12px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.6);
  padding: 2px 6px;
  border-radius: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 11px;
  max-width: 150px;

  svg {
    font-size: 12px;
    margin-right: 4px;
    color: #2e7d5b;
    flex-shrink: 0;
  }
`;

const CheckIconContainer = styled.div<{
  isProjected: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  //hover
  &:hover {
    svg {
      color: ${(props) =>
        props.isProjected ? "#2e7d5b" : "rgba(151 91 0 / 0.94)"};
    }
  }
`;

const RemoveIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 2px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.8);
  }

  svg {
    font-size: 12px;
  }
`;

const AssignedRoleBadge = ({
  refetch,
  tourGroupId,
  assigneeUsd,
}: AssignedRoleBadgeProps) => {
  const { data: entities } = useFetchEntities() as any;

  const tourGroupInUsd = assigneeUsd?.tourGroups.find(
    (tourGroup: any) => tourGroup?.id === tourGroupId
  );

  const user = entities.data.staff.find(
    (s: any) => s._id === assigneeUsd?.user
  );

  const { mutateAsync: addOrUpdateUserDaySchedule } =
    useAddOrUpdateUserDaySchedule() as unknown as {
      isLoading: boolean;
      data: ScheduleData | null;
      mutateAsync: (data: ScheduleData) => Promise<ScheduleData>;
    };

  const { mutateAsync: removeTourGroupFromSchedule } =
    useRemoveTourGroupFromUserSchedule() as any;

  return (
    <BadgeContainer $isProjected={tourGroupInUsd?.projected}>
      <CheckIconContainer
        isProjected={tourGroupInUsd?.projected}
        onClick={async () => {
          try {
            const newTourGroup = {
              ...tourGroupInUsd,
              projected: !tourGroupInUsd?.projected,
            };
            const newTourGroups = assigneeUsd?.tourGroups.map(
              (tourGroup: any) =>
                tourGroup.id === tourGroupId ? newTourGroup : tourGroup
            );

            await addOrUpdateUserDaySchedule({
              ...assigneeUsd,
              _id: assigneeUsd?._id,
              tourGroups: newTourGroups,
              notify: true,
            });

            refetch();
          } catch (error) {
            alert(
              "Error removing tour group from user schedule. Please try again."
            );

            console.error(
              "Error removing tour group from user schedule:",
              error
            );
          }
        }}
      >
        <CheckCircleIcon
          color={tourGroupInUsd?.projected ? "warning" : "success"}
          fontSize="small"
        />
      </CheckIconContainer>

      <UserInfo>
        <PersonIcon />
        <span>{user?.name}</span>
      </UserInfo>
      <Tooltip title={"Remove from schedule"}>
        <RemoveIconContainer
          onClick={async () => {
            try {
              await removeTourGroupFromSchedule({
                tour_group_id: tourGroupId,
                schedule_id: assigneeUsd?._id,
                assignee_id: assigneeUsd?.user,
                notify: true,
              });

              refreshStaffScheduleById.value = assigneeUsd?._id;

              refetch();
            } catch (error) {
              alert(
                "Error removing tour group from user schedule. Please try again."
              );

              console.error(
                "Error removing tour group from user schedule:",
                error
              );
            }
          }}
        >
          <CloseIcon color="error" />
        </RemoveIconContainer>
      </Tooltip>
    </BadgeContainer>
  );
};

export default AssignedRoleBadge;
